import useIdentity from "@/features/ui/hooks/use-identity";
import React, { useCallback, useEffect, useMemo } from "react";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import useGetDenseOrderConfirmationInvoice, {
  GetDenseOrderConfirmationInvoiceParameter,
  GetDenseOrderConfirmationInvoiceRequest,
} from "@/features/invoices/order-confirmation-invoices/hooks/use-get-dense-order-confirmation-invoice";
import { Formik } from "formik";
import { Grid } from "@chakra-ui/react";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import OrderConfirmationInvoiceForm from "@/features/invoices/order-confirmation-invoices/order-confirmation-invoice-form";
import {
  Brand,
  Company,
  isStoredObject,
  Price,
  StoredObject,
} from "@/features/types";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import { useOrderConfirmationInvoiceDumbTransaction } from "@/features/invoices/order-confirmation-invoices/hooks/use-order-confirmation-invoice-dumb-transaction";
import useUpdateOrderConfirmationInvoice, {
  UpdateOrderConfirmationInvoiceRequest,
} from "@/features/invoices/order-confirmation-invoices/hooks/use-update-order-confirmation-invoice";
import useOrderConfirmationInvoiceFormValidationSchema from "@/features/invoices/order-confirmation-invoices/hooks/use-order-confirmation-invoice-form-validation-schema";
import useRequiredParams from "@/features/invoices/hooks/use-required-params";
import {
  AbstractInvoice,
  OrderConfirmationInvoice,
  PaymentMethod,
} from "@/features/invoices/order-confirmation-invoices/order-confirmation-invoice.type";
import { DenseOrderSheetSet, LightOrderSheetSet } from "@/features/order-sheets/order-sheet.type";
import useReadOrderConfirmationInvoice from "@/features/invoices/order-confirmation-invoices/hooks/use-read-order-confirmation-invoice";
import InvoiceDetailPageHeader from "@/features/invoices/bank-remittance-receipts/invoice-detail-page-header";
import { PriceInputValue } from "@/features/invoices/app-price-input";
import useDumbMixedUpdate from "@/features/transportations/use-dumb-mixed-update";
import PriceUtils from "@/features/ui/utils/price-utils";

interface OrderConfirmationInvoiceDetailPageProps {
  isEditing?: boolean;
}

export interface OrderConfirmationInvoiceFormFields {
  id?: number;
  number?: string;
  status?: OrderConfirmationInvoice["status"];
  isRemittedToBoutique?: boolean;
  isRemittedToAgency?: boolean;
  name?: string;
  season?: string;
  orderedBy?: Pick<Company, "id" | "name">;
  issuedBy?: Company;
  // 3자무역 OC 의 원래 부티크·수수료율 — 이 화면에서는 바꾸지 않고 그대로 돌려보낸다
  originallyIssuedBy?: Company;
  crossTradeFeeRate?: number;
  issuedThrough?: Pick<Company, "id" | "name">;
  issuedOn?: Date;
  paymentMethod?: PaymentMethod;
  exposedToBuyer: boolean;
  paidByAgency: boolean;
  publicComment?: string | null;
  privateComment?: string | null;
  orderSheetSets: LightOrderSheetSet[];
  file?: File | Pick<StoredObject, "id" | "name">;
  // 인보이스 총액·총 디파짓 — 담당자가 인보이스 금액을 그대로 넣는다(브랜드별 금액은 서버가 비율로 나눈다)
  totalAmount?: PriceInputValue;
  totalRequiredAmount?: PriceInputValue;
  orderConfirmationInvoiceDetailList: (Omit<
    OrderConfirmationInvoice["orderConfirmationInvoiceDetailList"][number],
    | "amount"
    | "requiredAmount"
    | "brand"
    | "orderSheetSet"
    | "remittedAmount"
    | "expectedRemainingDepositAmount"
    | "actualRemainingDepositAmount"
  > & {
    amount?: PriceInputValue;
    requiredAmount?: PriceInputValue;
    brand?: Pick<Brand, "id" | "name">;
    orderSheetSet?: {
      id: number;
      lineSheetSet: {
        id: number;
        name: string;
      };
    };
  })[];
  proformaInvoices: { id: number }[];
  expectedRemainingDepositAmount?: PriceInputValue;
  creditInvoices: { id: number }[];
  depositInvoice?: { id: number };
  payments: {
    id: number;
    status: AbstractInvoice["status"];
    sender: Pick<Company, "id" | "name">;
    // 수정 가능 판정(order-confirmation-invoice-form)에 쓴다 — 서버 payments 를 그대로 넣는다
    remittedAmount?: Price;
    privateComment?: string | null;
  }[];

  exchangeRate?: number;
  bankFee?: PriceInputValue
}

export default function OrderConfirmationInvoiceDetailPage({
  isEditing = false,
}: OrderConfirmationInvoiceDetailPageProps) {
  const { error: showError } = useAppToasts();
  const { orderConfirmationInvoiceId } = useRequiredParams<{
    orderConfirmationInvoiceId: number;
  }>();
  const { navigate } = useRouterHelper();
  const identity = useIdentity();
  const { t, tTitle } = useI18nHelper();

  const {
    fire: fireUpdateOrderConfirmationInvoice,
    data: updateOrderConfirmationInvoiceData,
    isLoading: updateOrderConfirmationInvoiceIsLoading,
    error: updateOrderConfirmationInvoiceError,
    clear: clearUpdateOrderConfirmationInvoice,
  } = useUpdateOrderConfirmationInvoice();


  const getDenseOrderConfirmationInvoiceKey = useMemo(():
    | {
        parameter: GetDenseOrderConfirmationInvoiceParameter;
        request: GetDenseOrderConfirmationInvoiceRequest;
      }
    | undefined => {
    if (identity?.company && orderConfirmationInvoiceId !== undefined) {
      return {
        parameter: {
          by: identity.company.type,
          companyId: identity.company.id,
          orderConfirmationInvoiceId,
        },
        request: {},
      };
    }

    return undefined;
  }, [orderConfirmationInvoiceId, identity]);

  const {
    data: orderConfirmationInvoice,
    error: getOrderConfirmationInvoiceError,
    mutate: mutateGetOrderConfirmationInvoice,
  } = useGetDenseOrderConfirmationInvoice(
    getDenseOrderConfirmationInvoiceKey?.parameter,
    getDenseOrderConfirmationInvoiceKey?.request
  );


  const {
    isLoading: isReadOrderConfirmationInvoiceLoading,
    data: readOrderConfirmationInvoiceData,
    clear: clearReadOrderConfirmationInvoice,
    fire: fireReadOrderConfirmationInvoice,
  } = useReadOrderConfirmationInvoice();

  useEffect(() => {
    if (
      identity?.company?.type === "BUYER" &&
      orderConfirmationInvoice &&
      !orderConfirmationInvoice.isRead
    ) {
      fireReadOrderConfirmationInvoice(
        {
          by: identity.company.type,
          companyId: identity.company.id,
          orderConfirmationInvoiceId: orderConfirmationInvoice.id,
        },
        {}
      );
    }
  }, [fireReadOrderConfirmationInvoice, identity, orderConfirmationInvoice]);

  useEffect(() => {
    if (readOrderConfirmationInvoiceData) {
      mutateGetOrderConfirmationInvoice().then(() => {
        clearReadOrderConfirmationInvoice();
      });
    }
  }, [
    clearReadOrderConfirmationInvoice,
    mutateGetOrderConfirmationInvoice,
    readOrderConfirmationInvoiceData,
  ]);

  const validateSchema = useOrderConfirmationInvoiceFormValidationSchema();

  const validate = useCallback(
    async (values: OrderConfirmationInvoiceFormFields) => {
      try {
        await validateSchema.validate(values, { abortEarly: false });
      } catch (e) {
        showError(e);
        return e;
      }
    },
    [showError, validateSchema]
  );

  const initialValues = useMemo((): OrderConfirmationInvoiceFormFields => {
    if (orderConfirmationInvoice) {
      return {
        id: orderConfirmationInvoice.id,
        number: orderConfirmationInvoice.number || undefined,
        status: orderConfirmationInvoice.status,
        season: orderConfirmationInvoice.season,
        isRemittedToBoutique: orderConfirmationInvoice.isRemittedToBoutique,
        isRemittedToAgency: orderConfirmationInvoice.isRemittedToAgency,
        orderedBy: orderConfirmationInvoice.issuedToCompany,
        issuedBy: orderConfirmationInvoice.issuedByCompany,
        originallyIssuedBy: orderConfirmationInvoice.originallyIssuedByCompany ?? undefined,
        crossTradeFeeRate: orderConfirmationInvoice.crossTradeFeeRate ?? undefined,
        issuedThrough: orderConfirmationInvoice.issuedThroughCompany,
        issuedOn: orderConfirmationInvoice.issuedOn,
        paymentMethod: orderConfirmationInvoice.paymentMethod,
        paidByAgency: orderConfirmationInvoice.paidBy === "AGENCY",
        exposedToBuyer: orderConfirmationInvoice.isPublic,
        privateComment: orderConfirmationInvoice.privateComment,
        publicComment: orderConfirmationInvoice.publicComment,
        orderSheetSets: orderConfirmationInvoice.orderSheetSets,
        file: orderConfirmationInvoice.file || undefined,
        name: orderConfirmationInvoice.name,
        orderConfirmationInvoiceDetailList:
          orderConfirmationInvoice.orderConfirmationInvoiceDetailList.map(
            (ocDetail) => {
              const orderSheetSet = ocDetail.orderSheetSet
                ? orderConfirmationInvoice.orderSheetSets.find(
                    (oss) => oss.id === ocDetail.orderSheetSet?.id
                  )
                : undefined;

              return {
                ...ocDetail,
                orderSheetSet,
              };
            }
          ),
        proformaInvoices: orderConfirmationInvoice.proformaInvoices.map(
          (pInvoice) => {
            return {
              id: pInvoice.id,
            };
          }
        ),
        expectedRemainingDepositAmount:
          orderConfirmationInvoice.expectedRemainingDepositAmount,

        creditInvoices: orderConfirmationInvoice.creditInvoices.map(
          (cInvoice) => {
            return {
              id: cInvoice.id,
            };
          }
        ),
        depositInvoice: orderConfirmationInvoice.depositInvoice || undefined,
        totalAmount: orderConfirmationInvoice.totalAmount,
        totalRequiredAmount: orderConfirmationInvoice.totalRequiredAmount,
        payments: orderConfirmationInvoice.payments,
        exchangeRate: orderConfirmationInvoice.exchangeRate || 1,
        bankFee: orderConfirmationInvoice.bankFee! as Price,
      };
    }

    return {
      exposedToBuyer: identity?.company?.type !== "BOUTIQUE",
      paidByAgency: false,
      orderSheetSets: [],
      orderConfirmationInvoiceDetailList: [],
      proformaInvoices: [],
      creditInvoices: [],
      payments: [],
    };
  }, [orderConfirmationInvoice, identity]);

  const { isUpdateQueued, handleSubmit } = useDumbMixedUpdate({
    dumbFunction: useOrderConfirmationInvoiceDumbTransaction,
    smartFunction: useUpdateOrderConfirmationInvoice,
    isSmart: (request): request is UpdateOrderConfirmationInvoiceRequest => {
      return isStoredObject(request.file);
    },
    toRequest: (values: OrderConfirmationInvoiceFormFields) => {
      return {
        id: values.id!,
        number: values.number!,
        season: values.season!,
        status: values.status!,
        name: values.name!,
        issuedToCompany: values.orderedBy!,
        issuedByCompany: values.issuedBy!,
        originallyIssuedByCompany:
          values.issuedBy?.type === "CROSS_TRADER" ? values.originallyIssuedBy ?? null : null,
        // 3자무역 수수료율은 더 이상 쓰지 않는다(디파짓을 인보이스 금액 그대로 넣는다)
        crossTradeFeeRate: null,
        issuedThroughCompany: values.issuedThrough!,
        issuedOn: values.issuedOn!,
        paidBy: values.paidByAgency ? ("AGENCY" as const) : ("BUYER" as const),
        paymentMethod: values.paymentMethod!,
        // 브랜드별 금액은 보내지 않는다 — 서버가 기존 브랜드 행을 잇고 총액·총 디파짓을 비율로 다시 나눈다(2026-10-30)
        orderConfirmationInvoiceDetailList: [],
        orderSheetSets: values.orderSheetSets.map((oss) => {
          return {
            id: oss.id,
          };
        }),
        isPublic: values.exposedToBuyer,
        publicComment: values.publicComment || null,
        privateComment: values.privateComment || null,
        file: values.file!,
        totalAmount: PriceUtils.placeholder(values.totalAmount, "EUR") as Price,
        totalRequiredAmount: PriceUtils.placeholder(values.totalRequiredAmount, "EUR") as Price,
        exchangeRate: values.exchangeRate || null,
        bankFee: PriceUtils.placeholder(values.bankFee, "KRW"),
      };
    },
    mutate: mutateGetOrderConfirmationInvoice,
    defaultRedirectUrl: `/order-confirmation-invoices/${orderConfirmationInvoiceId}`,
  });

  return (
    <Formik<OrderConfirmationInvoiceFormFields>
      initialValues={initialValues}
      validate={validate}
      validateOnChange={false}
      validateOnBlur={false}
      validateOnMount={false}
      enableReinitialize={true}
      onSubmit={async (values, formikHelpers) => {
        if (identity?.company) {
          return await handleSubmit(
            {
              by: identity.company.type,
              companyId: identity.company.id,
              orderConfirmationInvoiceId: values.id!,
            },
            values,
            formikHelpers
          );
        } else {
          return false;
        }
      }}
    >
      {(props) => {
        return (
          <Grid
            as={"form"}
            height={"100%"}
            width={"100%"}
            gridTemplateColumns={"1fr"}
            gridTemplateRows={"auto auto 1fr"}
          >
            <InvoiceDetailPageHeader
              formik={props}
              isUpdateQueued={
                identity?.company && props.values.id
                  ? isUpdateQueued({
                      by: identity.company.type,
                      companyId: identity.company.id,
                      orderConfirmationInvoiceId: props.values.id,
                    })
                  : false
              }
              title={tTitle("order_confirmation_invoice")}
              baseUrl={"/order-confirmation-invoices"}
              isEditing={isEditing}
            />
            <OrderConfirmationInvoiceForm
              formik={props}
              isEditing={isEditing}
            />
          </Grid>
        );
      }}
    </Formik>
  );
}
